export const MUSEUM_STORAGE_KEY = 'immutables.museum.settings.v1';

export function defaultMuseumSettings(projectId, networkId) {
  return { version: 1, networkId: Number(networkId || 1), name: 'My exhibition',
    projects: projectId ? [{ projectId: Number(projectId), editions: '' }] : [],
    wallets: '', seconds: 20, gridSeconds: 10, showGrid: true, order: 'random', style: 'artist', customCSS: '',
    labels: false, interaction: true, mode: 'screen' };
}

export function parseEditions(value) {
  if (!String(value || '').trim()) return null;
  const ids = new Set();
  for (const part of String(value).trim().split(/[,\s]+/)) {
    const match = part.match(/^(\d+)(?:-(\d+))?$/);
    if (!match) throw new Error('Use edition numbers such as 1, 3, 7-12.');
    const start = Number(match[1]), end = Number(match[2] || match[1]);
    if (!Number.isSafeInteger(start) || start < 1 || end < start || end > 1000000 || end - start > 10000) {
      throw new Error('Check the edition range. Use positive numbers in ascending ranges.');
    }
    for (let id = start; id <= end; id++) ids.add(id);
    if (ids.size > 10000) throw new Error('Choose at most 10,000 editions per project.');
  }
  return ids;
}

export function parseWallets(value) {
  const wallets = String(value || '').trim().split(/[,\s]+/).filter(Boolean);
  if (wallets.some(w => !/^0x[\da-f]{40}$/i.test(w))) throw new Error('Enter full Ethereum wallet addresses, separated by commas or new lines.');
  return [...new Set(wallets.map(w => w.toLowerCase()))];
}

export function validateMuseumSettings(input, networkId) {
  if (!input || input.version !== 1 || Number(input.networkId) !== Number(networkId)) throw new Error('Choose an exhibition file for this network.');
  if (!Array.isArray(input.projects) || !input.projects.length || input.projects.length > 100) throw new Error('Select at least one project.');
  const projects = input.projects.map(p => {
    if (!Number.isSafeInteger(Number(p.projectId)) || Number(p.projectId) < 1) throw new Error('Invalid project selection.');
    const editions = String(p.editions || '');
    if (editions.length > 20000) throw new Error('The edition selection is too long.');
    parseEditions(editions);
    return { projectId: Number(p.projectId), editions };
  });
  if (new Set(projects.map(p => p.projectId)).size !== projects.length) throw new Error('A project appears more than once.');
  parseWallets(input.wallets);
  const seconds = Number(input.seconds);
  if (!Number.isFinite(seconds) || seconds < 5 || seconds > 86400) throw new Error('Choose 5 to 86,400 seconds per edition.');
  const gridSeconds = input.gridSeconds == null ? 10 : Number(input.gridSeconds);
  if (!Number.isFinite(gridSeconds) || gridSeconds < 2 || gridSeconds > 86400) throw new Error('Choose 2 to 86,400 seconds for the grid.');
  if (!['random', 'sequence', 'shuffle'].includes(input.order) || !['artist', 'black', 'white', 'custom'].includes(input.style) || !['screen', 'projection'].includes(input.mode)) throw new Error('Check the exhibition presentation settings.');
  const customCSS = String(input.customCSS || '');
  if (customCSS.length > 50000) throw new Error('Keep the exhibition CSS under 50,000 characters.');
  return { ...defaultMuseumSettings(null, networkId), projects, seconds, gridSeconds, showGrid: input.showGrid !== false, name: String(input.name || 'My exhibition').slice(0, 120),
    wallets: String(input.wallets || ''), order: input.order, style: input.style, customCSS,
    labels: input.labels === true, interaction: input.interaction !== false, mode: input.mode };
}

export function exhibitionOrder(entries, shuffle, random = Math.random) {
  const result = entries.slice();
  if (shuffle) for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

export async function prepareMuseumExhibition(settings, projects, provider, onProgress = () => {}, isCancelled = () => false) {
  const selected = validateMuseumSettings(settings, settings.networkId);
  const wallets = parseWallets(selected.wallets);
  const catalogue = {};
  const entries = [];
  const check = () => { if (isCancelled()) throw new Error('Selection cancelled.'); };
  for (const rule of selected.projects) {
    check();
    const project = projects[rule.projectId - 1];
    if (!project) throw new Error('Project ' + rule.projectId + ' is unavailable in this viewer.');
    if (!['p5js', 'threejs', 'vanillajs', 'glsl'].includes(project.scriptType)) throw new Error(project.name + ' does not have a supported live renderer.');
    if (selected.mode === 'projection' && project.scriptType !== 'p5js') throw new Error('Projection mapping currently supports p5 projects. Use single screen for ' + project.name + '.');
    onProgress('Reading ' + project.name + '…');
    const allTokens = (await provider.getProjectTokens(rule.projectId, project.currentEditionId)).filter(Boolean);
    check();
    const wanted = parseEditions(rule.editions);
    if (!wanted && allTokens.length !== Number(project.currentEditionId)) throw new Error(project.name + ' has an incomplete edition list. Refresh the data or choose specific available editions.');
    const tokens = allTokens.filter(t => !wanted || wanted.has(Number(t.editionId))).sort((a, b) => Number(a.editionId) - Number(b.editionId));
    if (wanted) {
      const available = new Set(tokens.map(t => Number(t.editionId)));
      const missing = [...wanted].filter(id => !available.has(id));
      if (missing.length) throw new Error(project.name + ': editions ' + missing.slice(0, 8).join(', ') + ' are unavailable. Update your selection.');
    }
    if (!tokens.length) throw new Error(project.name + ' has no available editions.');
    if (tokens.some(t => !/^0x[\da-f]{64}$/i.test(t.transactionHash || ''))) throw new Error(project.name + ' has missing mint inputs. Refresh its data before exhibiting.');
    let included = tokens;
    if (wallets.length) {
      let next = 0, done = 0;
      const matches = new Set(), unknown = [];
      await Promise.all(Array.from({ length: Math.min(4, tokens.length) }, async () => {
        while (next < tokens.length) {
          check();
          const token = tokens[next++];
          let timer;
          const owner = await Promise.race([
            provider.getOwnerOfToken(token.tokenId).catch(() => null),
            new Promise(resolve => { timer = setTimeout(() => resolve(null), 10000); })
          ]).finally(() => clearTimeout(timer));
          check();
          if (!owner) unknown.push(token.editionId);
          else if (wallets.includes(owner.toLowerCase())) matches.add(String(token.tokenId));
          onProgress('Checking collection wallets: ' + (++done) + ' / ' + tokens.length + ' in ' + project.name);
        }
      }));
      if (unknown.length) throw new Error('Ownership is unavailable for ' + unknown.length + ' editions in ' + project.name + '. Refresh ownership data or select editions without a wallet filter.');
      included = tokens.filter(t => matches.has(String(t.tokenId)));
    }
    if (!included.length) continue;
    const source = await provider.getProjectScript(project.scriptTransactionHash);
    check();
    if (!source.script) throw new Error('The live source for ' + project.name + ' is unavailable.');
    const details = await provider.getProjectDetails(rule.projectId);
    let css = '';
    if (selected.style === 'artist') {
      css = await provider.getProjectCSS(details.projectIdToImageURLBase, false, rule.projectId);
      if (!css) {
        const posts = await provider.getPagePosts(source.page, source.blockNumber);
        const styles = (posts || []).filter(p => ['style', 'style.immutables.art'].includes(p.tag) && p.content)
          .sort((a, b) => (b.blockNumber || 0) - (a.blockNumber || 0));
        css = styles.length ? styles[0].content : '';
      }
    }
    check();
    catalogue[rule.projectId] = { project, script: source.script, css, details };
    included.forEach(token => entries.push({ ...token, projectId: rule.projectId }));
  }
  if (!entries.length) throw new Error('No editions match these projects and collection wallets. Change the selection.');
  return { settings: selected, catalogue, entries: exhibitionOrder(entries, selected.order === 'shuffle'),
    preparedAt: new Date().toISOString(), ownersAsOf: provider.ownersCapturedAt || null };
}
